import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
	Button,
	Col,
	Form,
	Input,
	PageHeader,
	Row,
	Select,
	Table,
	Tooltip,
} from "antd";
import moment from "moment-jalaali";

import { CheckPermission, GetUrlParams, ObjectToURL } from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { Modals, Permissions } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/PaymentVerificationModal";

const Component = ({ onSetRoute, routeName, parentName, permissions }) => {
	const initialState = { data: [], pagination: {}, users: [] };
	const tableColumns = [
		{
			title: Resources.row,
			dataIndex: "index",
			className: "center-cell",
			width: "60px",
			render: (value, record, index) => index + 1,
		},
		{
			title: Resources.code,
			dataIndex: "code",
			className: "center-cell",
		},
		{
			title: Resources.job,
			dataIndex: "job",
			className: "center-cell",
			render: (value) => value.title || "-",
		},

		{
			title: Resources.subject,
			dataIndex: "subject",
			className: "center-cell",
			render: (value) =>
				value === "ladder"
					? `${Resources.action} ${Resources.ladder}${Resources.y}`
					: value === "subscription"
					? `${Resources.buy} ${Resources.subscription}`
					: `${Resources.jobs} ${Resources.special}`,
		},
		{
			title: Resources.amount,
			dataIndex: "amount",
			className: "center-cell",
		},

		{
			title: Resources.depositedTo,
			dataIndex: "depositedTo",
			className: "center-cell",
		},
		{
			title: `${Resources.code} ${Resources.trace}`,
			dataIndex: "traceNumber",
			className: "center-cell",
		},
		{
			title: Resources.user,
			dataIndex: "user",
			className: "center-cell",
			render: (value) => value?.fullName || "-",
		},
		{
			title: Resources.status,
			dataIndex: "status",
			className: "center-cell color-cell",
			render: (value) => {
				switch (value) {
					case "pending":
						return <span className="blue">{Resources.pending}</span>;
					case "approved":
						return <span className="green">{Resources.approved}</span>;
					case "rejected":
						return <span className="red">{Resources.rejected}</span>;
				}
			},
		},
		{
			title: `${Resources.date} ${Resources.pay}`,
			dataIndex: "paymentDate",
			className: "center-cell desc-cell ltr-cell",
			width: "100px",
			render: (value) => {
				if (value) {
					const [date, time] = moment(value)
						.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
						.split(" ");

					return (
						<>
							<span className="name">{date}</span>
						</>
					);
				} else {
					return Resources.unlimited;
				}
			},
		},
		{
			title: Resources.action,
			dataIndex: "id",
			className: "center-cell",
			width: "120px",
			render: (value, record) => (
				<>
					{record?.status === "pending" && permissions.status && (
						<Tooltip title={`${Resources.change} ${Resources.status}`}>
							<Button
								type="link"
								icon={<i className="fa fa-pencil-alt" />}
								onClick={() => handleForm(record)}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
	const [state, setState] = useState(initialState);
	const { data, fetching, loading, pagination, selected, users } = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const statuses = [
		{ value: "approved", label: Resources.approved },
		{ value: "pending", label: Resources.pending },
		{ value: "rejected", label: Resources.rejected },
	];

	const fetchData = () => {
		const params = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue(params);

		setState((prevState) => ({ ...prevState, loading: true }));

		API.paymentVerification
			.list({ ...params, pageSize: process.env.REACT_APP_PAGE_SIZE })
			.then(({ data, meta: { currentPage, perPage, total } }) => {
				setState((prevState) => ({
					...prevState,
					data,
					pagination: {
						current: currentPage,
						pageSize: perPage,
						total,
						showSizeChanger: false,
					},
					loading: false,
				}));
			})
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleForm = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(`${pathname}${search}#${Modals.CREATE}`);
		}
	};

	const handleTable = ({ current }) => {
		const params = GetUrlParams(search);
		const query = ObjectToURL({ ...params, page: current });

		navigate(`${pathname}?${query}`);
	};

	const handleFilter = debounce(() => {
		form.validateFields().then((values) => {
			const search = ObjectToURL({ page: 1, ...values });

			navigate(`${pathname}?${search}`);
		});
	}, 1000);

	const userFilter = debounce((e) => {
		setState((prevState) => ({ ...prevState, fetching: true }));
		API.user
			.list({ search: e })
			.then(({ data }) =>
				setState((prevState) => ({
					...prevState,
					users: data.map(({ id, phone, fullName }) => ({
						value: id,
						label: fullName || phone,
					})),
					fetching: false,
				}))
			)
			.catch(() => {});
	}, 1000);

	const componentDidUpdate = () => {
		fetchData();
	};

	const componentDidMount = () => {
		setState((prevState) => ({ ...prevState, fetching: true }));

		API.user
			.list({ pageSize: process.env.REACT_APP_PAGE_SIZE })
			.then(({ data }) =>
				setState((prevState) => ({
					...prevState,
					users: data.map(({ id, phone, fullName }) => ({
						value: id,
						label: fullName || phone,
					})),
					fetching: false,
				}))
			);
		onSetRoute({ openKey: parentName, selectedKey: routeName });
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader title={`${Resources.pay}${Resources.ha}`} />
			<Form layout="vertical" form={form}>
				<Row gutter={24}>
					<Col xs={24} sm={12} md={8} lg={6} xxl={4}>
						<Form.Item name="search">
							<Input
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								placeholder={Resources.search}
								allowClear
								autoFocus
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} md={8} lg={6} xxl={4}>
						<Form.Item name="subject">
							<Select
								placeholder={Resources.subject}
								options={[
									{
										value: "ladder",
										label: `${Resources.action} ${Resources.ladder}${Resources.y}`,
									},
									{
										value: "subscription",
										label: `${Resources.buy} ${Resources.subscription}`,
									},
									{
										value: "specialJobs",
										label: `${Resources.jobs} ${Resources.special}`,
									},
								]}
								onChange={handleFilter}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} md={8} lg={6} xxl={4}>
						<Form.Item name="status">
							<Select
								placeholder={Resources.status}
								options={statuses}
								onChange={handleFilter}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} md={8} lg={6} xxl={4}>
						<Form.Item name="user">
							<Select
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								onSearch={(e) => userFilter(e)}
								loading={fetching}
								optionFilterProp="label"
								placeholder={Resources.user}
								options={users}
								showSearch
								allowClear
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				onChange={handleTable}
				pagination={pagination}
				scroll={{ x: 930 }}
				rowKey="id"
			/>
			<FormModal data={selected} onUpdate={handleForm} options={{ statuses }} />
		</>
	);
};

export default connect(
	(state) => {
		return {
			permissions: {
				status: CheckPermission(
					state.user.permissions,
					Permissions.PAYMENT_STATUS
				),
			},
		};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
